import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ConsentService } from './consent.service';
import { SeoApiService } from './seo-api.service';

type Twq = ((...args: unknown[]) => void) & {
  exe?: (...args: unknown[]) => void;
  queue?: unknown[][];
  version?: string;
  loaded?: boolean;
};

@Injectable({ providedIn: 'root' })
export class XPixelService {
  private lastPath = '';
  private started = false;
  private scriptLoaded = false;
  private pixelId = '';

  constructor(
    private readonly router: Router,
    private readonly consent: ConsentService,
    private readonly seoApi: SeoApiService,
  ) {}

  start(): void {
    if (this.started || !this.consent.hasConsent()) {
      return;
    }
    this.started = true;
    this.seoApi.publico().subscribe((config) => {
      const id = config.rastreamento.x_pixel_id?.trim() ?? '';
      if (!id || !this.consent.hasConsent()) {
        return;
      }
      this.pixelId = id;
      this.garantirScript();
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => this.onNavigate(event.urlAfterRedirects));

      if (this.router.navigated) {
        this.onNavigate(this.router.url);
      }
    });
  }

  private onNavigate(url: string): void {
    if (!this.consent.hasConsent() || !this.pixelId) {
      return;
    }
    const path = this.pathOf(url);
    if (path === this.lastPath) {
      return;
    }
    this.lastPath = path;
    this.twq('event', 'PageView');
  }

  private garantirScript(): void {
    if (typeof document === 'undefined' || this.scriptLoaded || !this.pixelId) {
      return;
    }
    const w = window as Window & { twq?: Twq };
    if (typeof w.twq === 'function') {
      this.scriptLoaded = true;
      return;
    }
    this.scriptLoaded = true;
    const twq: Twq = function (...args: unknown[]) {
      if (twq.exe) {
        twq.exe(...args);
      } else {
        (twq.queue = twq.queue || []).push(args);
      }
    };
    twq.version = '1.1';
    twq.queue = [];
    w.twq = twq;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://static.ads-twitter.com/uwt.js';
    document.head.appendChild(script);
    twq('config', this.pixelId);
  }

  private pathOf(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    if (path.length > 1 && path.endsWith('/')) {
      return path.slice(0, -1);
    }
    return path || '/';
  }

  private twq(...args: unknown[]): void {
    const fn = (window as Window & { twq?: Twq }).twq;
    if (typeof fn === 'function') {
      fn(...args);
    }
  }
}
